import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowRight, Database, Globe, Mail, Network, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { ApiError } from "@hostdock/sdk";
import type { MigrationDetail, MigrationSite } from "@hostdock/types";
import { AppLogo, Badge, Button, Checkbox, ConfirmDialog, ErrorState, Field, FormError, Notice, Select, Skeleton, toast } from "@hostdock/ui";
import { api } from "../api";
import { DetailCard } from "../components/page-kit";
import { KIND_LABEL, ReadySummary, ResourceRow, TransferAccessForm } from "./migrate";

type Mode = MigrationSite["mode"];
interface Choice { mode: Mode; websiteId?: string | null; aliasOf?: string | null }

/** Groups shown on the selection page, in the order a customer thinks about their account. */
const GROUPS: { title: string; icon: typeof Globe; kinds: string[] }[] = [
  { title: "Websites", icon: Globe, kinds: ["FILES", "DOMAIN", "SUBDOMAIN", "PHP_CONFIG", "CRON_JOB"] },
  { title: "Databases", icon: Database, kinds: ["DATABASE", "DB_USER"] },
  { title: "Email", icon: Mail, kinds: ["EMAIL_ACCOUNT", "FORWARDER"] },
  { title: "SSL certificates", icon: ShieldCheck, kinds: ["SSL"] },
];

/**
 * Account migration: each site of the old account gets its own HostDock website (or joins one), then the customer
 * picks what to move. Plan capacity is shown before anything is created; the API enforces it again.
 */
export function AccountSelectionStep({ m, onChange }: { m: MigrationDetail; onChange(m: MigrationDetail): void }) {
  const wordpress = m.provider === "wordpress" || m.provider === "wordpress-admin";
  const opts = useQuery({ queryKey: ["hosting-options"], queryFn: api.hosting.options });
  const sitesQ = useQuery({ queryKey: ["websites", "all"], queryFn: () => api.websites.list({ pageSize: 100 }) });
  const subs = useMemo(() => opts.data?.subscriptions ?? [], [opts.data]);
  const [subId, setSubId] = useState(m.options.subscriptionId ?? "");
  useEffect(() => { if (!subId && subs.length === 1) setSubId(subs[0]!.id); }, [subs, subId]);
  const sub = subs.find((s) => s.id === subId);
  const free = sub ? Math.max(0, sub.limits.websites - sub.usage.websites) : 0;
  const existing = (sitesQ.data?.items ?? []).filter((w) => w.status === "ACTIVE");

  const [choices, setChoices] = useState<Record<string, Choice>>(() => Object.fromEntries(m.sites.map((s) => [s.domain, { mode: s.mode, websiteId: s.websiteId, aliasOf: s.aliasOf }])));
  // First visit: as many sites as the plan has room for become new websites; the rest join the main site.
  const [defaulted, setDefaulted] = useState(m.sites.some((s) => s.websiteId || s.mode !== "new"));
  useEffect(() => {
    if (defaulted || !sub) return;
    const main = m.sites.find((s) => s.main) ?? m.sites[0];
    let left = free;
    setChoices(Object.fromEntries(m.sites.map((s) => {
      if (left > 0) { left--; return [s.domain, { mode: "new" as Mode }]; }
      return [s.domain, main && main.domain !== s.domain && free > 0 ? { mode: "alias" as Mode, aliasOf: main.domain } : { mode: "skip" as Mode }];
    })));
    setDefaulted(true);
  }, [defaulted, sub, free, m.sites]);

  const set = (domain: string, c: Choice) => setChoices((x) => ({ ...x, [domain]: c }));
  const creating = Object.values(choices).filter((c) => c.mode === "new").length - m.sites.filter((s) => s.mode === "new" && s.websiteId).length;
  const over = creating > free;
  const hosts = m.sites.filter((s) => ["new", "existing"].includes(choices[s.domain]?.mode ?? ""));
  const incomplete = m.sites.some((s) => {
    const c = choices[s.domain];
    return !c || (c.mode === "existing" && !c.websiteId) || (c.mode === "alias" && !hosts.some((h) => h.domain === c.aliasOf));
  });

  // What to move: everything that can be moved, except the folders of sites that are not getting a website.
  const movable = m.resources.filter((r) => r.transferable);
  const [picked, setPicked] = useState<Set<string>>(() => new Set(movable.map((r) => r.id)));
  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const skippedFolder = (r: MigrationDetail["resources"][number]) => r.kind === "FILES" && m.sites.some((s) => r.name.startsWith(`${s.domain} (`) && !["new", "existing"].includes(choices[s.domain]?.mode ?? ""));
  const pickedItems = m.resources.filter((r) => picked.has(r.id) && !skippedFolder(r));
  const needsTransfer = pickedItems.some((r) => r.requires.includes("TRANSFER_ACCESS"));
  const accessReady = !needsTransfer || m.access.transfer !== null;
  const hasMail = pickedItems.some((r) => r.kind === "EMAIL_ACCOUNT" || r.kind === "FORWARDER");
  const [verify, setVerify] = useState(m.options.verifyDomains);
  const [confirm, setConfirm] = useState(false);
  const notMoved = m.resources.filter((r) => !r.transferable);

  const start = useMutation({ meta: { inlineError: true },
    mutationFn: async () => {
      await api.migrations.select(m.id, {
        resourceIds: pickedItems.map((r) => r.id), filesMode: "merge",
        account: { subscriptionId: subId, verifyDomains: verify, sites: m.sites.map((s) => ({ domain: s.domain, ...choices[s.domain]! })) },
      });
      return api.migrations.start(m.id);
    },
    onSuccess: (r) => { setConfirm(false); onChange(r); },
  });

  // A WordPress site is copied over the SFTP login the customer signed in with; there is no separate copy sign-in.
  const showTransfer = needsTransfer && !wordpress;

  return (
    <>
      {wordpress ? (
        <Notice tone="success" title="Connected to your WordPress site">
          {m.provider === "wordpress-admin"
            ? <>Signed in to WordPress as {String((m.account as { username?: string }).username ?? m.usernameHint)} and installed HostDock's temporary migration plugin{(m.account as { panelVersion?: string }).panelVersion ? <> (found {String((m.account as { panelVersion?: string }).panelVersion)})</> : null}. It copies the files and the database, and removes itself when the migration finishes. Choose where the site goes, then start.</>
            : <>Signed in over SFTP as {String((m.account as { username?: string }).username ?? m.usernameHint)}. HostDock found WordPress and read its database settings from wp-config.php. Choose where the site goes, then start.</>}
        </Notice>
      ) : (
        <Notice tone="success" title={`Connected to your ${m.providerLabel} account`}>
          Signed in as {String((m.account as { username?: string }).username ?? m.usernameHint)}. HostDock found {m.sites.length} website{m.sites.length === 1 ? "" : "s"} in this account. Choose where each one goes, then what to move.
        </Notice>
      )}
      {m.warnings.map((w) => <Notice key={w} tone="warning">{w}</Notice>)}

      <DetailCard title="1. Where each website goes" description="Each folder on your old host can become its own HostDock website, join a website you already have, or be added to another one as an extra domain.">
        {subs.length > 1 && (
          <Field label="Hosting package for new websites" className="mb-4 max-w-md">
            {(p) => <Select {...p} value={subId} onChange={(e) => setSubId(e.target.value)}><option value="">Choose a package…</option>{subs.map((s) => <option key={s.id} value={s.id}>{s.plan.name} ({s.usage.websites} of {s.limits.websites} websites used)</option>)}</Select>}
          </Field>
        )}
        {opts.isPending ? <Skeleton className="h-40 w-full rounded-xl" /> : opts.error ? <ErrorState error={opts.error} onRetry={() => void opts.refetch()} /> : !subs.length ? (
          <Notice tone="warning" title="You need a hosting package first">Websites are created in a hosting package. Contact support to get one, then come back.</Notice>
        ) : (
          <ul className="flex flex-col gap-3">
            {m.sites.map((s) => <SiteRow key={s.domain} site={s} choice={choices[s.domain] ?? { mode: "skip" }} onChange={(c) => set(s.domain, c)} hosts={hosts.filter((h) => h.domain !== s.domain)} existing={existing} />)}
          </ul>
        )}
        {sub && (
          <div className="mt-4">
            {over
              ? <Notice tone="danger" title={`${creating} new website(s), but your ${sub.plan.name} plan has room for ${free}`}>Add some sites to another website as an extra domain, put them into an existing website, skip them, or upgrade your plan.</Notice>
              : <p className="text-[12.5px] text-muted">{creating} new website{creating === 1 ? "" : "s"} will be created · your {sub.plan.name} plan has room for {free}.</p>}
          </div>
        )}
      </DetailCard>

      <DetailCard title="2. What to move" description="Everything HostDock can move is selected. Items it cannot move are listed with the reason, so nothing is left behind silently." flush>
        {GROUPS.map((g) => {
          const items = m.resources.filter((r) => g.kinds.includes(r.kind) && r.transferable && !skippedFolder(r));
          if (!items.length) return null;
          return (
            <div key={g.title} className="border-b border-line last:border-0">
              <p className="flex items-center gap-2 bg-surface-2/60 px-4 py-2 text-[12px] font-semibold tracking-wide text-muted uppercase sm:px-5"><g.icon size={14} />{g.title} ({items.length})</p>
              <ul className="divide-y divide-line">{items.map((r) => <ResourceRow key={r.id} r={r} checked={picked.has(r.id)} onToggle={() => toggle(r.id)} />)}</ul>
            </div>
          );
        })}
        {notMoved.length > 0 && (
          <details className="border-t border-line px-4 py-3 sm:px-5">
            <summary className="cursor-pointer text-[13px] font-medium">Not moved automatically ({notMoved.length})</summary>
            <ul className="mt-2 divide-y divide-line">{notMoved.slice(0, 200).map((r) => <ResourceRow key={r.id} r={r} checked={false} onToggle={() => undefined} />)}</ul>
            {notMoved.some((r) => r.kind === "DNS_RECORD") && <p className="mt-2 text-[12.5px] text-muted">DNS records can be imported in one click from the report once the domain's DNS is connected in HostDock ({KIND_LABEL.DNS_RECORD}).</p>}
          </details>
        )}
      </DetailCard>

      {showTransfer && (
        <DetailCard title="3. Copy files and mail" description="HostDock copies your website files and mailboxes over a secure connection. The sign-in is stored encrypted and deleted when the migration ends.">
          <AccountTransferAccess m={m} onChange={onChange} />
        </DetailCard>
      )}

      <DetailCard title={showTransfer ? "4. Review and start" : "3. Review and start"}>
        {/* Only cPanel's adapter can add a DNS record on the old host; elsewhere the option would do nothing. */}
        {hasMail && m.provider === "cpanel" && (
          <div className="mb-4 rounded-xl border border-line p-3.5">
            <Checkbox
              checked={verify}
              onChange={setVerify}
              label="Verify my domains through the old host's DNS"
              description="Adds HostDock's verification TXT record to the DNS zone on your old cPanel, so your mailboxes can be created before you switch DNS. This is the only change made on your old host (besides a temporary database user while copying databases)."
            />
          </div>
        )}
        {!accessReady && <Notice tone="warning">Set up the copy sign-in above, or untick the files and mailboxes.</Notice>}
        <FormError error={start.error} />
        <div className="mt-3 flex justify-end">
          <Button variant="primary" disabled={!sub || over || incomplete || !accessReady || pickedItems.length === 0} onClick={() => setConfirm(true)}>Review and start</Button>
        </div>
      </DetailCard>

      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="Ready to migrate"
        tone="primary"
        confirmLabel="Start migration"
        loading={start.isPending}
        error={start.error}
        description="Check the summary, then start. The migration runs in the background."
        onConfirm={() => start.mutateAsync()}
      >
        <ReadySummary m={m} items={pickedItems} destination={`${hosts.length} website(s) on HostDock, ${creating} of them new`} />
        <ul className="list-disc space-y-1 pl-5 text-[12.5px] text-muted">
          <li>Your websites keep working on the old host until you change DNS; the report tells you exactly what to change.</li>
          {hasMail && <li>Mailboxes keep their current passwords. Mail that arrives on the old host later can be copied again from the report.</li>}
          <li>Imported cron jobs stay paused until you enable them.</li>
        </ul>
      </ConfirmDialog>
    </>
  );
}

function SiteRow({ site, choice, onChange, hosts, existing }: { site: MigrationSite; choice: Choice; onChange(c: Choice): void; hosts: MigrationSite[]; existing: { id: string; primaryDomain: string }[] }) {
  const value = choice.mode === "alias" ? `alias:${choice.aliasOf}` : choice.mode;
  const pick = (v: string) => (v.startsWith("alias:") ? onChange({ mode: "alias", aliasOf: v.slice(6) }) : onChange({ mode: v as Mode, websiteId: v === "existing" ? (choice.websiteId ?? existing[0]?.id ?? null) : null }));
  return (
    <li className="flex flex-col gap-3 rounded-xl border border-line p-3.5 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <AppLogo appType={site.wordpress ? "WORDPRESS" : "PHP"} size="md" />
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 text-[14px] font-semibold break-all">{site.domain}{site.main && <Badge tone="accent">Main site</Badge>}</p>
          <p className="text-[12.5px] text-muted">{site.wordpress ? "WordPress" : "PHP website"}{site.phpVersion ? ` · PHP ${site.phpVersion}` : ""}{site.databases.length ? ` · ${site.databases.length} database${site.databases.length === 1 ? "" : "s"}` : ""}</p>
          {site.aliases.length > 0 && <p className="mt-0.5 text-[12px] break-all text-subtle">Also serves {site.aliases.join(", ")}</p>}
          {site.documentRoot && <p className="mt-0.5 font-mono text-[11.5px] break-all text-subtle">{site.documentRoot}</p>}
        </div>
      </div>
      <div className="flex w-full shrink-0 flex-col gap-2 sm:w-64">
        <Select aria-label={`Where ${site.domain} goes`} value={value} onChange={(e) => pick(e.target.value)}>
          <option value="new">New HostDock website</option>
          {existing.length > 0 && <option value="existing">Into an existing website…</option>}
          {hosts.map((h) => <option key={h.domain} value={`alias:${h.domain}`}>Extra domain of {h.domain}</option>)}
          <option value="skip">Don't move this site</option>
        </Select>
        {choice.mode === "existing" && (
          <Select aria-label={`Existing website for ${site.domain}`} value={choice.websiteId ?? ""} onChange={(e) => onChange({ mode: "existing", websiteId: e.target.value })}>
            {existing.map((w) => <option key={w.id} value={w.id}>{w.primaryDomain}</option>)}
          </Select>
        )}
      </div>
    </li>
  );
}

/** The panel password doubles as the copy sign-in; with an API token or key the customer enters a password instead. */
function AccountTransferAccess({ m, onChange }: { m: MigrationDetail; onChange(m: MigrationDetail): void }) {
  const viaToken = (m.account as { authScheme?: string }).authScheme === "token";
  const [manual, setManual] = useState(viaToken);
  const [protocol, setProtocol] = useState<"sftp" | "ftps">("sftp");
  // Some hosts run SSH on another port (Hostinger 65002, SiteGround 18765, Namecheap 21098, A2 Hosting 7822).
  const [port, setPort] = useState("");
  // HostDock reads the server's SSH key when this is saved and accepts only that key for the rest of the migration.
  const save = useMutation({ meta: { inlineError: true },
    mutationFn: () => api.migrations.setAccess(m.id, { transfer: { useAccountLogin: true, protocol, ...(port.trim() ? { port: Number(port) } : {}) } }),
    onSuccess: (r) => { onChange(r); toast.success(`HostDock will copy with your ${m.providerLabel} login`); },
  });
  const saved = m.access.transfer;
  if (manual) return <TransferAccessForm m={m} onChange={onChange} />;
  return (
    <div className="flex flex-col gap-4">
      {saved && <Notice tone="success">Ready: copying over {saved.protocol.toUpperCase()} as {saved.usernameHint}.</Notice>}
      <div className="grid gap-3 sm:grid-cols-2">
        {(["sftp", "ftps"] as const).map((p) => (
          <button key={p} type="button" aria-pressed={protocol === p} onClick={() => setProtocol(p)} className={`rounded-xl border p-3.5 text-left transition-colors ${protocol === p ? "border-accent bg-accent-soft/60" : "border-line hover:border-line-strong"}`}>
            <span className="block text-[14px] font-semibold">{p === "sftp" ? "SFTP (recommended)" : "FTPS"}</span>
            <span className="block text-[12.5px] text-muted">{p === "sftp" ? `Encrypted over SSH. Needs SSH access enabled for your ${m.providerLabel} account.` : "FTP over TLS. Use this if your account has no SSH access."}</span>
          </button>
        ))}
      </div>
      {protocol === "sftp" && (
        <label className="flex max-w-[16rem] items-center gap-2 text-[12.5px] text-muted">
          SSH port
          <input aria-label="SSH port" inputMode="numeric" value={port} placeholder="22" onChange={(e) => setPort(e.target.value.replace(/\D/g, ""))} className="h-8 w-24 rounded-md border border-line bg-surface px-2 text-fg" />
        </label>
      )}
      <FormError error={save.error} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={() => setManual(true)}>Use a different sign-in</Button>
        <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Use my {m.providerLabel} login</Button>
      </div>
    </div>
  );
}

interface AccountReport {
  sites?: { websiteId: string; domain: string; created: boolean; status: string }[];
  dnsZones?: string[];
  mailboxes?: string[];
  dbUsers?: { source: string; username: string | null }[];
}

/** Report sections that only account migrations have: the websites, DNS import, copying mail again, database users. */
export function AccountReportExtras({ m, onChange }: { m: MigrationDetail; onChange(m: MigrationDetail): void }) {
  const report = m.report as AccountReport;
  const resync = useMutation({ meta: { inlineError: true }, mutationFn: () => api.migrations.resyncEmail(m.id), onSuccess: (r) => { onChange(r); toast.success("Copying mail again"); } });
  const [confirmResync, setConfirmResync] = useState(false);
  const zones = useMemo(() => report.dnsZones ?? [], [report.dnsZones]);
  return (
    <>
      {report.sites && report.sites.length > 0 && (
        <DetailCard title="Your websites on HostDock" flush>
          <ul className="divide-y divide-line">
            {report.sites.map((s) => (
              <li key={s.websiteId} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5">
                <span className="min-w-0"><span className="block font-medium break-all">{s.domain}</span><span className="text-[12.5px] text-muted">{s.created ? "Created by this migration" : "Existing website"}</span></span>
                <Link to={`/websites/${s.websiteId}`} className="inline-flex items-center gap-1 text-[13px] font-semibold text-accent hover:underline">Open website <ArrowRight size={14} /></Link>
              </li>
            ))}
          </ul>
        </DetailCard>
      )}
      {report.mailboxes && report.mailboxes.length > 0 && (
        <DetailCard title="Email" description={`${report.mailboxes.length} mailbox(es) moved with their current passwords.`}>
          <p className="text-[13px] text-muted">Once you have pointed your domains' MX records at HostDock, copy the mail again to bring over anything that reached the old host in the meantime. Messages already copied are not duplicated.</p>
          {!m.credentialsStored && <p className="mt-2 text-[12.5px] text-subtle">The stored sign-in has been deleted, so mail can no longer be copied from this migration.</p>}
          <div className="mt-3 flex justify-end"><Button disabled={!m.credentialsStored} loading={resync.isPending} onClick={() => setConfirmResync(true)} icon={<Mail size={14} />}>Copy mail again</Button></div>
          <FormError error={resync.error} />
          <ConfirmDialog open={confirmResync} onOpenChange={setConfirmResync} tone="primary" title="Copy mail again?" description="New mail on the old host is copied into the same mailboxes. Nothing on HostDock is removed." confirmLabel="Copy mail" loading={resync.isPending} onConfirm={() => resync.mutateAsync().then(() => setConfirmResync(false))} />
        </DetailCard>
      )}
      {report.dbUsers && report.dbUsers.length > 0 && (
        <DetailCard title="Database users" description="Recreated with access to the migrated databases. Their passwords are new: set one with Reset password in the website's Databases tab.">
          <ul className="divide-y divide-line text-[13px]">
            {report.dbUsers.map((u) => <li key={u.source} className="flex flex-wrap items-center justify-between gap-2 py-2"><span className="font-mono">{u.source}</span><span className="flex items-center gap-1.5 font-mono text-muted"><ArrowRight size={13} />{u.username ?? "—"}</span></li>)}
          </ul>
        </DetailCard>
      )}
      {zones.length > 0 && (
        <DetailCard title="DNS records" description="Copy the records from your old host into HostDock DNS. Records that pointed at the old server are pointed at HostDock instead; records that already exist are left alone.">
          <ul className="divide-y divide-line">{zones.map((z) => <DnsImportRow key={z} m={m} zone={z} />)}</ul>
        </DetailCard>
      )}
    </>
  );
}

function DnsImportRow({ m, zone }: { m: MigrationDetail; zone: string }) {
  const run = useMutation({ meta: { inlineError: true },
    mutationFn: () => api.migrations.importDns(m.id, zone),
    onSuccess: (r) => toast.success(`${zone}: ${r.created} record(s) imported${r.rewritten ? `, ${r.rewritten} pointed at HostDock` : ""}${r.failed.length ? `, ${r.failed.length} failed` : ""}`),
  });
  const notConnected = run.error instanceof ApiError && run.error.code === "DNS_ZONE_NOT_CONNECTED";
  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
      <span className="flex items-center gap-2 font-medium"><Network size={15} className="text-subtle" />{zone}</span>
      <div className="flex flex-wrap items-center gap-2">
        {notConnected && <Link to="/dns" className="text-[12.5px] font-semibold text-accent hover:underline">Connect {zone} in DNS first →</Link>}
        <Button size="sm" loading={run.isPending} onClick={() => run.mutate()}>{run.isSuccess ? "Import again" : "Import records"}</Button>
      </div>
      {run.error && !notConnected && <div className="w-full"><FormError error={run.error} /></div>}
      {run.data?.failed.length ? <p className="w-full text-[12px] text-danger">Not imported: {run.data.failed.map((f) => `${f.type} ${f.name} (${f.reason})`).join("; ")}</p> : null}
    </li>
  );
}
